import React, { useEffect, useRef, useState } from 'react';
import { User } from '@phosphor-icons/react';
import Avatar from '../../../design-system/Avatar';
import { Link } from '../../Link';
import { Msg, useFormatMessage } from '../../../i18n-review/client/Msg';
import { useAuthOptional } from '../../../contexts/AuthContext';
import { useMyJobsBadgeCount } from '../../../hooks/useMyJobsBadgeCount';
import { normalizeSupportedLocale } from '../../../utils/routing/locale-from-pathname';
import { ValueSheet } from './ValueSheet';
import type { PrimaryNavLocale } from '../primary-nav-links';
import { ACCOUNT_AVATAR_GUEST, ACCOUNT_AVATAR_MEMBER, ACCOUNT_BADGE, ACCOUNT_PILL, NAV_PART } from './nav-styles';

const PILL_CLASSNAME = ACCOUNT_PILL;

/**
 * Row-one account control for the navbar live-index redesign (TEC navbar redesign Phase 1):
 * one pill instead of the old MyJobsNavEntry + separate "Sign in" + the avatar living inside the
 * menu button. Signed in, it's the avatar + "My Jobs" + its live badge, linking to /tools same as
 * MyJobsNavEntry today. Signed out, it's the same pill shape with a dashed empty avatar slot;
 * clicking it opens the guest value sheet instead of navigating.
 *
 * Keeps the existing "render user only after mount" pattern (see UserMenu.tsx) so SSR and first
 * client paint always render the guest pill, avoiding a hydration mismatch for signed-in users.
 */
export const AccountPill: React.FC<{ locale: PrimaryNavLocale }> = ({ locale }) => {
  const formatMessage = useFormatMessage();
  const auth = useAuthOptional();
  const badgeCount = useMyJobsBadgeCount();
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);
  const isAuthenticated = hasMounted && Boolean(auth?.isAuthenticated);

  const [sheetOpen, setSheetOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!sheetOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setSheetOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSheetOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [sheetOpen]);

  const myJobsLabel = (
    <Msg id="hub.nav.myJobs" defaultMessage="My Jobs" description="Persistent header entry linking to the unified My Jobs hub" />
  );

  if (isAuthenticated) {
    const user = auth?.user;
    const avatarUrl =
      user &&
      (user.image ??
        user.picture ??
        (user as { avatar?: string })?.avatar ??
        (user as { avatar_url?: string })?.avatar_url);
    return (
      <Link href="/tools" locale={normalizeSupportedLocale(locale)} className={PILL_CLASSNAME} data-jc-nav-part={NAV_PART.accountPill}>
        <span className={ACCOUNT_AVATAR_MEMBER} data-jc-nav-part={NAV_PART.accountAvatar}>
          <Avatar src={avatarUrl ?? null} size={30} name={user?.name || 'User'} />
        </span>
        {myJobsLabel}
        {badgeCount > 0 ? (
          <span className={ACCOUNT_BADGE}>{badgeCount > 99 ? '99+' : badgeCount}</span>
        ) : null}
      </Link>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={sheetOpen}
        aria-label={formatMessage({
          id: 'hub.nav.myJobs',
          defaultMessage: 'My Jobs',
          description: 'Persistent header entry linking to the unified My Jobs hub',
        })}
        onClick={() => setSheetOpen((open) => !open)}
        className={PILL_CLASSNAME}
        data-jc-nav-part={NAV_PART.accountPill}
      >
        <span className={ACCOUNT_AVATAR_GUEST} data-jc-nav-part={NAV_PART.accountAvatar}>
          <User className="w-3.5 h-3.5" aria-hidden />
        </span>
        {myJobsLabel}
      </button>
      {sheetOpen ? <ValueSheet locale={locale} onRequestClose={() => setSheetOpen(false)} /> : null}
    </div>
  );
};
